import {
    Autocomplete,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    type AutocompleteProps,
    type SelectChangeEvent,
} from '@mui/material'
import {
    CandidateFilterCategory,
    RosterStatus,
    SearchIndexField,
} from '__generated__/graphql'
import { startCase } from 'lodash'
import { useMemo, useState } from 'react'
import { useRosterSearch } from 'recruiter/hooks'

const statusOptions: RosterStatus[] = Object.values(RosterStatus)

interface RosterStatusFilterProps {
    compact?: boolean
}

export function RosterStatusFilter({
    compact = false,
}: RosterStatusFilterProps) {
    const { tableState, setTableState } = useRosterSearch()

    const statusFilter = tableState.filters.find(
        (f) => f.facet === SearchIndexField.RosterStatus
    )

    const statusesInFilter = useMemo(() => {
        const statusLabels = statusFilter?.value?.stringArray ?? []
        return statusOptions.filter((status) => statusLabels.includes(status))
    }, [statusFilter])

    const handleChange = (newValues: RosterStatus[]) => {
        const otherFilters = tableState.filters.filter(
            (f) => f.facet !== SearchIndexField.RosterStatus
        )

        const newFilters = [...otherFilters]

        if (newValues.length > 0) {
            newFilters.push({
                facet: SearchIndexField.RosterStatus,
                category: CandidateFilterCategory.All,
                value: { stringArray: newValues },
            })
        }

        setTableState({ filters: newFilters })
    }

    const handleAutocompleteChange: AutocompleteProps<
        (typeof statusOptions)[number],
        true,
        false,
        false
    >['onChange'] = (_, newValues) => {
        handleChange(newValues)
    }

    const handleSelectChange = (event: SelectChangeEvent<RosterStatus[]>) => {
        const value = event.target.value
        handleChange(typeof value === 'string' ? [] : value)
    }

    const [open, setOpen] = useState(false)

    if (compact) {
        return (
            <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                    multiple
                    open={open}
                    onOpen={() => setOpen(true)}
                    value={statusesInFilter}
                    onChange={handleSelectChange}
                    label="Status"
                    renderValue={(selected) =>
                        selected.length > 0 ? `${selected.length} selected` : ''
                    }
                    MenuProps={{
                        onClose: (_, reason) => {
                            if (
                                reason === 'backdropClick' ||
                                reason === 'escapeKeyDown'
                            ) {
                                setOpen(false)
                            }
                        },
                    }}
                >
                    {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                            <Checkbox
                                checked={statusesInFilter.includes(status)}
                                size="small"
                            />
                            <ListItemText primary={startCase(status)} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        )
    }

    return (
        <Autocomplete
            ChipProps={{ size: 'small' }}
            multiple
            onChange={handleAutocompleteChange}
            options={statusOptions}
            getOptionLabel={(option) => startCase(option)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Status"
                    placeholder="Search statuses..."
                />
            )}
            value={statusesInFilter}
        />
    )
}
